import { useEffect, useState } from 'react';

import type { ScriptProgress } from '../lib/api.js';
import { useScriptRun } from '../lib/useScriptRun.js';

/**
 * WHAT THE BREAKDOWN IS DOING, while it is doing it.
 *
 * A producer pressed a button that costs money and then watched a page that
 * said nothing. The run took half a minute, finished, wrote fifty-six
 * requirements — and the screen still read "no location requirement
 * identified" on every row with one line of grey text above it claiming the
 * work was "still running". Asked what was wrong, the honest answer was
 * "nothing, look at the database". That is the failure this fixes: not a bug in
 * the pipeline, a silence in the product.
 *
 * IT IS INDETERMINATE, AND THAT IS NOT A SHORTCUT. The locations come from one
 * request for the whole screenplay (`screenplay-packing.ts`) and each scene's
 * elements from about a hundred more, and nothing is written until all of them
 * have answered, so there is no partial answer to report: a percentage would be
 * an invented number, and a per-scene counter would describe progress nobody
 * can see. What IS true and worth showing is the phase, how
 * long it has been going, and that leaving the page costs nothing — so that is
 * what it shows.
 */

/** The phases a producer can actually distinguish, from the run's own record. */
function phaseOf(progress: ScriptProgress): { title: string; detail: string } {
  const extract = progress.stages.find((s) => s.stage === 'EXTRACT');

  if (progress.status === 'QUEUED' || extract === undefined) {
    return {
      title: 'Queued',
      // Said plainly: a queue is not a stall, and the difference matters to
      // somebody deciding whether to press the button again.
      detail: 'Waiting for a worker to pick it up. This is normally a second or two.',
    };
  }

  return {
    title: 'Reading your screenplay',
    detail:
      'The locations and every scene’s elements all appear together when the reading finishes, which takes a few minutes for a feature-length script — nothing is missing while you wait.',
  };
}

/** Elapsed, in the words somebody would use out loud. */
function elapsedLabel(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;

  const minutes = Math.floor(seconds / 60);

  return `${String(minutes)}m ${String(seconds % 60)}s`;
}

export function BreakdownProgress({
  scriptVersionId,
  testId = 'breakdown-progress',
}: {
  scriptVersionId: string | null;
  testId?: string;
}) {
  // Read-only: the screens that own the data re-read it when the run ends.
  const { progress, inFlight } = useScriptRun(scriptVersionId);

  /**
   * A SECOND HAND, so a long wait reads as a long wait rather than as a hang.
   *
   * Counted from the run's own `startedAt` where it has one, so the figure
   * survives a reload and a tab opened half way through — a timer that restarts
   * at zero on every mount would tell a producer who reloaded that nothing had
   * happened yet.
   */
  const startedAt = progress?.startedAt ?? null;
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!inFlight) return;

    const from = startedAt === null ? Date.now() : new Date(startedAt).getTime();
    const tick = () => {
      setSeconds(Math.max(0, Math.round((Date.now() - from) / 1000)));
    };

    tick();
    const timer = setInterval(tick, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [inFlight, startedAt]);

  if (!inFlight || progress === null) return null;

  const phase = phaseOf(progress);

  return (
    <div
      className="card1"
      data-testid={testId}
      data-phase={progress.status}
      style={{ padding: '18px 20px', marginTop: 18 }}
      // One live region, polite: the phase changes at most twice in a run, and
      // the seconds are deliberately OUTSIDE it — a screen reader announcing a
      // number every second is unusable.
      aria-live="polite"
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <p className="colh" style={{ margin: 0 }}>
          {phase.title}
        </p>
        <span
          className="mm"
          data-testid="breakdown-progress-elapsed"
          aria-hidden="true"
          style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', marginLeft: 'auto' }}
        >
          {elapsedLabel(seconds)}
        </span>
      </div>

      <div className="sa-sweep-track" style={{ marginTop: 12 }} role="presentation">
        <div className="sa-sweep-bar" />
      </div>

      <p className="mm" style={{ fontSize: 12.5, margin: '12px 0 0', lineHeight: 1.6 }}>
        {phase.detail}
      </p>
      <p className="mm" style={{ fontSize: 12.5, margin: '6px 0 0', lineHeight: 1.6 }}>
        It runs on the server — you can leave this page and come back.
      </p>
    </div>
  );
}
